import { useState, useEffect } from "react";
import {
  Plus,
  X,
  School,
  Trash2,
  Edit2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { type SchoolProfileData } from "../../../lib/api";

const COLORS = [
  "#1967d2",
  "#d93025",
  "#188038",
  "#f9ab00",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#009688",
  "#795548",
  "#607d8b",
  "#202124",
];

interface SchoolProfileManagerProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: "list" | "form";
}

export default function SchoolProfileManager({
  isOpen,
  onClose,
  initialView = "list",
}: SchoolProfileManagerProps) {
  const { profiles, addProfile, editProfile, removeProfile, refreshProfiles } =
    useSchoolProfile();
  const [view, setView] = useState<"list" | "form">(initialView);
  const [editingProfile, setEditingProfile] =
    useState<SchoolProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    institution: "",
    city: "",
    color: COLORS[0],
  });

  useEffect(() => {
    if (editingProfile) {
      setFormData({
        name: editingProfile.name,
        institution: editingProfile.institution || "",
        city: editingProfile.city || "",
        color: editingProfile.color || COLORS[0],
      });
      setView("form");
    } else {
      setFormData({
        name: "",
        institution: "",
        city: "",
        color: COLORS[0],
      });
    }
  }, [editingProfile]);

  useEffect(() => {
    if (initialView === "form") {
      setEditingProfile(null);
      setView("form");
    }
  }, [initialView]);

  useEffect(() => {
    if (!isOpen) {
      setView("list");
      setEditingProfile(null);
    }
  }, [isOpen]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setLoading(true);
    setError(null);
    try {
      if (editingProfile) {
        await editProfile(editingProfile.id, formData);
      } else {
        await addProfile(formData);
      }
      setView("list");
      setEditingProfile(null);
    } catch (err) {
      setError("Erro ao salvar perfil. Tente novamente.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (
      !window.confirm(
        `Deseja realmente excluir o perfil da escola "${name}"? Todos os dados associados serão perdidos.`,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await removeProfile(id);
    } catch (err) {
      alert("Erro ao excluir perfil.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-(--card-bg) rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-(--border-color) flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-(--border-color) flex items-center justify-between bg-(--bg-secondary)">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-(--primary-color)/10 flex items-center justify-center">
              <School className="w-5 h-5 text-(--primary-color)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-(--text-primary)">
                {view === "list"
                  ? "Minhas Escolas"
                  : editingProfile
                    ? "Editar Escola"
                    : "Nova Escola"}
              </h2>
              <p className="text-xs text-(--text-tertiary)">
                {view === "list"
                  ? "Gerencie seus perfis escolares"
                  : "Preencha os dados da escola"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-(--bg-accent) rounded-lg transition"
          >
            <X className="w-5 h-5 text-(--text-tertiary)" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {view === "list" ? (
            <div className="space-y-4">
              {profiles.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-(--bg-accent) rounded-full flex items-center justify-center mx-auto mb-3">
                    <Plus className="w-8 h-8 text-(--text-tertiary)" />
                  </div>
                  <p className="text-(--text-secondary) font-medium">
                    Nenhuma escola cadastrada
                  </p>
                  <p className="text-xs text-(--text-tertiary) mb-4">
                    Adicione uma escola para começar a organizar seus materiais.
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {profiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="group flex items-center justify-between p-4 rounded-xl border border-(--border-color) bg-(--bg-secondary) hover:border-(--primary-color) transition-all"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
                          style={{
                            backgroundColor:
                              profile.color || "var(--primary-color)",
                          }}
                        >
                          <School className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-(--text-primary) truncate">
                            {profile.name}
                          </p>
                          <p className="text-xs text-(--text-tertiary) truncate">
                            {profile.institution || "Professor"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingProfile(profile)}
                          className="p-2 text-(--text-tertiary) hover:text-(--primary-color) hover:bg-(--bg-accent) rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!profile.is_default && profiles.length > 1 && (
                          <button
                            onClick={() =>
                              handleDelete(profile.id, profile.name)
                            }
                            className="p-2 text-(--text-tertiary) hover:text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => {
                  setEditingProfile(null);
                  setView("form");
                }}
                className="w-full mt-2 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-(--border-color) rounded-xl text-(--text-tertiary) hover:text-(--primary-color) hover:border-(--primary-color) hover:bg-(--bg-accent)/50 transition-all font-medium"
              >
                <Plus className="w-5 h-5" />
                Adicionar Nova Escola
              </button>
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-1.5">
                  Nome da Escola/Identificador *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Escola Estadual Machado de Assis"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border rounded-xl outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary) focus:ring-2 focus:ring-(--primary-color)/20 focus:border-(--primary-color) transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-1.5">
                  Instituição (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Ensino Médio"
                  value={formData.institution}
                  onChange={(e) =>
                    setFormData({ ...formData, institution: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border rounded-xl outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary) focus:ring-2 focus:ring-(--primary-color)/20 focus:border-(--primary-color) transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-1.5">
                  Cidade (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ex: São Paulo - SP"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border rounded-xl outline-none bg-(--input-bg) border-(--border-color) text-(--text-primary) focus:ring-2 focus:ring-(--primary-color)/20 focus:border-(--primary-color) transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-3">
                  Cor de Identificação
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                        formData.color === color
                          ? "ring-2 ring-offset-2 ring-(--primary-color) scale-110"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                    >
                      {formData.color === color && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setView("list");
                    setEditingProfile(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl font-medium text-(--text-secondary) bg-(--bg-accent) hover:bg-(--bg-secondary) border border-(--border-color) transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  className="flex-1 px-4 py-2.5 bg-(--primary-color) text-white rounded-xl font-bold hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-(--primary-color)/20"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    "Salvar"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
